import { useEffect, useState } from "react";
import { ButtonGroup, Spinner } from "react-bootstrap";
import { Machine } from "../../../utils/models/Machine";
import { useTranslation } from "react-i18next";
import { MachineConected } from "aw-consts";
import { hourDiff } from "aw-date-functions";
import { createColumnHelper, SortingState } from "@tanstack/react-table";
import { formatNumberWithLeadingZeros } from "aw-generic-functions";
import { Instalation } from "../../../utils/models/Instalation";
import DataGrid from "../../aw-data-grid/DataGrid";

const MachineGrid = ({
    machines: machinesProp,
    onEditClick,
    onDeleteClick,
    onMachineClick,
    edit,
    loading,
    pagination,
    setPagination,
    total,
    filters,
    setFilters,
    sorting,
    setSorting,
    manual = false,
    instalations,
    globalFilter,
    onGlobalFilterChange
}: {
    machines: Machine[];
    onEditClick: (c: Machine) => void;
    onDeleteClick: (c: Machine) => void;
    onMachineClick: (c: Machine) => void;
    edit: boolean;
    loading: boolean;
    pagination: { pageIndex: number; pageSize: number };
    setPagination: React.Dispatch<React.SetStateAction<{ pageIndex: number; pageSize: number }>>;
    total: number;
    filters: any;
    setFilters: any;
    sorting: SortingState;
    setSorting: React.Dispatch<React.SetStateAction<any>>;
    manual?: boolean; // si vols que la taula sigui manual (sense paginació automàtica)
    instalations: Instalation[];
    globalFilter?: any;
    onGlobalFilterChange?: React.Dispatch<React.SetStateAction<any>>;
}) => {
    const [machines, setMachines] = useState<Machine[]>([]);
    const { t } = useTranslation();
    useEffect(() => {
        setMachines([...machinesProp]);
    }, [machinesProp]);

    const isMachineConected = (machine: Machine) => {
        if (
            machine &&
            new Date(machine.last_conection).getTime() === new Date(machine.createdAt).getTime()
        ) {
            return MachineConected.NoInicilized;
        } else if (machine && hourDiff(new Date(machine.last_conection))) {
            return MachineConected.Conected;
        }
        return MachineConected.Disconected;
    };

    const machineStateText = (state: MachineConected) => {
        switch (state) {
            case MachineConected.Conected:
                return <span className="rounded-3 ps-1 pe-1 connected">{t("Connected")}</span>;
            case MachineConected.Disconected:
                return (
                    <span className="rounded-3 ps-1 pe-1 disconnected">{t("Disconnected")}</span>
                );
            case MachineConected.NoInicilized:
                return (
                    <span className="rounded-3 ps-1 pe-1 disconnected">{t("NoInicialized")}</span>
                );
            case MachineConected.OutOfService:
                return (
                    <span className="rounded-3 ps-1 pe-1 disconnected">{t("OutOfService")}</span>
                );
            default:
                return null;
        }
    };

    const columnHelper = createColumnHelper<Machine>();

    const columns = [
        columnHelper.accessor("idmachine", {
            header: t("Identifier"),
            enableSorting: true,
            enableColumnFilter: false,
            cell: info => formatNumberWithLeadingZeros(info.getValue(), 5),
        }),
        columnHelper.accessor("name", {
            header: t("Name"),
            enableSorting: true,

            cell: info => info.getValue(),
        }),
        columnHelper.accessor(row => row.instalation.name, {
            id: "instalation",
            header: t("Installation"),
            enableSorting: true,
            meta: {
                filterOptions: Array.from(
                    new Map(instalations.map(i => [i.name, { label: i.name, value: i.name }])).values()
                ),
            },
            cell: info => info.getValue(),
        }),
        columnHelper.accessor("serial_number", {
            header: t("NSerie"),
            enableSorting: true,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor("v_firmware_hmi", {
            header: "V. Firmware HMI",
            enableColumnFilter: false,
            enableSorting: true,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor("machineState", {
            header: t("State"),
            enableSorting: true,
            cell: ({ row }) => machineStateText(isMachineConected(row.original)),
            enableColumnFilter: false,
        }),
        columnHelper.display({
            id: "actions",
            header: "",
            enableSorting: false,
            enableColumnFilter: false,
            cell: ({ row }) => {
                const machine = row.original as Machine;
                return (
                    <div className="text-end">
                        <ButtonGroup>
                            <button
                                onClick={(e) => {e.stopPropagation(); onDeleteClick(machine)}}
                                className="btn-Simple"
                            >
                                <i className="bi bi-trash text-danger" />
                            </button>
                        </ButtonGroup>
                    </div>
                );
            },
        }),
    ];

    return (
        <div
            className={`p-2 rounded-1 ${!edit ? "dashboardGridMachine" : "editGridMachine"}`}
            id="gridMachineContainer"
            style={{ backgroundColor: "var(--background-secondary)" }}
        >
            {!edit && <p className="mt-1 h4 text-center">{t("Machines")}</p>}
            {loading ? (
                <div className="text-center pt-5">
                    <Spinner animation="border" variant="success" />
                </div>
            ) : (
                <DataGrid
                    data={machines}
                    columnFilters={filters}
                    onColumnFiltersChange={setFilters}
                    pagination={pagination}
                    pageCount={total}
                    onPaginationChange={setPagination}
                    columns={columns}
                    //header={null}
                    onRowClick={(machine) => onEditClick(machine)}
                    //pageSize={10}
                    sorting={sorting}
                    onSortingChange={setSorting}
                    manual={manual}
                    //globalSearch={false}
                    filtersPlaceholder={t('Search')}
                    globalFilter={globalFilter}
                    onGlobalFilterChange={onGlobalFilterChange}
                />
            )}
        </div>
    );
};

export default MachineGrid;
